import { Grid, makeStyles } from '@material-ui/core'
import HoveredActions from 'components/common/HoveredActions'
import OpenInNewTabIcon from 'components/common/icons/OpenInNewTabIcon'
import ProfilePicture from 'components/common/ProfilePicture'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import useCopilotPatientsList from 'components/CopilotControl/hooks/useCopilotPatientsList'
import PatientStatus from 'components/Patients/PatientStatus'
import { ROUTES } from 'consts'
import {
  HygieneScoreCategoryKeysByValue,
  HygieneTrends,
  RiskCategoryKeysByValue,
  getOralHygieneCategoryColor
} from 'consts/oralHygieneReportConsts'
import moment from 'moment'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { trackEvent } from 'utils/analyticsUtils'
import { getEnvironmentHostname } from 'utils/awsUtils'

const LAYOUT_STYLES = {
  patientInfo: { width: '30%' },
  lastScan: { width: '14%' },
  perioRisk: { width: '14%' },
  cavityRisk: { width: '14%' },
  hygieneRating: { width: '14%' },
  hygieneTrend: { width: '14%' }
}

// U+2195 defaults to emoji presentation on some platforms; U+FE0E forces the text glyph so it
// inherits the tone color and baseline like the two plain arrows.
const TREND_ARROWS = {
  [HygieneTrends.Improving]: '↑',
  [HygieneTrends.Receding]: '↓',
  [HygieneTrends.Inconsistent]: '\u2195\uFE0E'
}

const useStyles = makeStyles(() => ({
  patientItem: {
    position: 'relative',
    display: 'flex',
    padding: '16px 20px',
    alignItems: 'center',
    borderBottom: '1px solid #E9E9E9',
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  avatar: {
    maxWidth: 80
  },
  patientNameStatusContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: 12,
    minWidth: 0,
    flex: 1
  },
  patientName: {
    fontWeight: 500
  },
  columnLabel: {
    fontFamily: 'Dazzed',
    fontSize: 13,
    fontWeight: 400,
    color: '#727B8C',
    paddingBottom: 5
  },
  columnValue: {
    fontFamily: 'Dazzed',
    fontSize: 14,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },
  truncatedText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }
}))

const OralHygienePatientListItem = ({ patient, listType, pastXDays, index }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { getPatientStatusObject } = useCopilotPatientsList()
  const [isHovered, setIsHovered] = useState(false)

  const {
    id: patientId,
    name: patientName,
    profilePicture,
    statusKey,
    program,
    lastOhScanDate,
    lastHygieneScore,
    lastDentalRisk,
    lastPerioRisk,
    hygieneTrend
  } = patient

  // A category column renders its Figma label in its tone color; a missing value renders a neutral dash.
  const categoryColumn = categoryKey => ({
    value: categoryKey ? t(`pages.oralHygieneReport.patientsList.categories.${categoryKey}`) : '-',
    color: categoryKey ? getOralHygieneCategoryColor(categoryKey) : '#001335',
    suffix: TREND_ARROWS[categoryKey]
  })

  const columns = [
    {
      key: 'lastScan',
      value: lastOhScanDate ? moment(lastOhScanDate).format('MMM D, HH:mm') : '-',
      color: '#001335',
      style: LAYOUT_STYLES.lastScan
    },
    { key: 'perioRisk', ...categoryColumn(RiskCategoryKeysByValue[lastPerioRisk]), style: LAYOUT_STYLES.perioRisk },
    { key: 'cavityRisk', ...categoryColumn(RiskCategoryKeysByValue[lastDentalRisk]), style: LAYOUT_STYLES.cavityRisk },
    {
      key: 'hygieneRating',
      ...categoryColumn(HygieneScoreCategoryKeysByValue[lastHygieneScore]),
      style: LAYOUT_STYLES.hygieneRating
    },
    { key: 'hygieneTrend', ...categoryColumn(hygieneTrend), style: LAYOUT_STYLES.hygieneTrend }
  ]

  const actions = useMemo(
    () => [
      {
        key: 'openPatient',
        icon: <OpenInNewTabIcon />,
        tooltip: t('pages.oralHygieneReport.patientsList.actions.openPatient'),
        onClick: () => {
          trackEvent('Oral Hygiene Report - Patient opened from list', {
            view: listType,
            pastXDays,
            patientId
          })
          window.open(`${getEnvironmentHostname()}${ROUTES.PATIENTS}/${patientId}`, '_blank', 'noopener,noreferrer')
        }
      }
    ],
    [patientId, listType, pastXDays, t]
  )

  const patientStatus = getPatientStatusObject(statusKey, program)

  return (
    <Grid
      key={index}
      container
      className={classes.patientItem}
      tabIndex={0}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={event => !event.currentTarget.contains(event.relatedTarget) && setIsHovered(false)}
    >
      <Grid item style={LAYOUT_STYLES.patientInfo}>
        <Grid container alignItems="center" wrap="nowrap">
          <Grid item className={classes.avatar}>
            <ProfilePicture name={patientName} photo={profilePicture} size="medium" />
          </Grid>
          <Grid item className={classes.patientNameStatusContainer}>
            <Grid item>
              <DazzedParagraph14 className={[classes.truncatedText, classes.patientName].join(' ')}>
                {patientName}
              </DazzedParagraph14>
            </Grid>
            <Grid item>
              <PatientStatus status={patientStatus} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {columns.map(column => (
        <Grid item key={column.key} style={column.style}>
          <Grid container direction="column">
            <Grid item className={classes.columnLabel}>
              {t(`pages.oralHygieneReport.patientsList.columns.${column.key}`)}:
            </Grid>
            <Grid item className={classes.columnValue} style={{ color: column.color }}>
              <span>{column.value}</span>
              {column.suffix && <span aria-hidden="true">{column.suffix}</span>}
            </Grid>
          </Grid>
        </Grid>
      ))}
      <HoveredActions actions={actions} isOpen={isHovered} placement="top" />
    </Grid>
  )
}

export default OralHygienePatientListItem
